import * as Cesium from "cesium";

import { House, Minus, Plus } from "lucide-react";

interface CameraResetPose {
  latitude: number;
  longitude: number;
  altitude: number;
  heading?: number;
  pitch?: number;
  roll?: number;
}

interface ViewerZoomControlsProps {
  viewer: Cesium.Viewer | null;
  offsetLeftPx?: number;
  initialCameraPose?: CameraResetPose | null;
}

const ZOOM_RATIO = 0.2;
const MIN_ZOOM_DISTANCE = 10;
const ZOOM_DURATION_SECONDS = 0.35;
const HOME_RESET_DURATION_SECONDS = 1.1;

function calculerPasDeZoom(viewer: Cesium.Viewer): number {
  const hauteurCamera = viewer.camera.positionCartographic?.height;

  if (!Number.isFinite(hauteurCamera) || hauteurCamera <= 0) {
    return 100;
  }

  return Math.max(hauteurCamera * ZOOM_RATIO, MIN_ZOOM_DISTANCE);
}

function calculerDestinationZoom(
  viewer: Cesium.Viewer,
  direction: "in" | "out",
  distance: number,
): Cesium.Cartesian3 {
  const position = Cesium.Cartesian3.clone(viewer.camera.positionWC);
  const directionCamera = Cesium.Cartesian3.normalize(
    viewer.camera.directionWC,
    new Cesium.Cartesian3(),
  );
  const vecteurZoom = Cesium.Cartesian3.multiplyByScalar(
    directionCamera,
    direction === "in" ? distance : -distance,
    new Cesium.Cartesian3(),
  );

  return Cesium.Cartesian3.add(position, vecteurZoom, new Cesium.Cartesian3());
}

/**
 * Contrôles de caméra superposés au viewer Cesium.
 * Contient le bouton home et les contrôles de zoom (+/-) en ligne horizontale.
 * Le pas de zoom s'adapte à la hauteur de la caméra pour garder un ressenti fluide.
 */
export function ViewerZoomControls({
  viewer,
  offsetLeftPx = 16,
  initialCameraPose = null,
}: ViewerZoomControlsProps) {
  const zoomer = (direction: "in" | "out") => {
    if (!viewer || viewer.isDestroyed()) {
      return;
    }

    const distance = calculerPasDeZoom(viewer);
    const destination = calculerDestinationZoom(viewer, direction, distance);

    viewer.camera.cancelFlight();
    viewer.camera.flyTo({
      destination,
      duration: ZOOM_DURATION_SECONDS,
      easingFunction: Cesium.EasingFunction.QUADRATIC_OUT,
      orientation: {
        heading: viewer.camera.heading,
        pitch: viewer.camera.pitch,
        roll: viewer.camera.roll,
      },
    });
  };

  const revenirALaVueInitiale = () => {
    if (!viewer || viewer.isDestroyed() || !initialCameraPose) {
      return;
    }

    viewer.camera.cancelFlight();
    viewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(
        initialCameraPose.longitude,
        initialCameraPose.latitude,
        initialCameraPose.altitude,
      ),
      duration: HOME_RESET_DURATION_SECONDS,
      orientation: {
        heading: initialCameraPose.heading ?? 0,
        pitch: initialCameraPose.pitch ?? viewer.camera.pitch,
        roll: initialCameraPose.roll ?? 0,
      },
      easingFunction: Cesium.EasingFunction.QUADRATIC_OUT,
    });
  };

  return (
    <div
      className="absolute z-[9997] flex flex-row gap-2"
      style={{
        top: "calc(var(--viewer-topbar-height, 80px) + 100px)",
        left: `${offsetLeftPx}px`,
        transition: "left 300ms ease, top 300ms ease",
      }}
      aria-label="Contrôles de caméra"
    >
      {/* Bouton Home */}
      <button
        type="button"
        onClick={revenirALaVueInitiale}
        className="flex h-12 w-12 cursor-pointer items-center justify-center rounded-2xl border border-white/25 bg-slate-950/28 text-white/92 shadow-[0_8px_18px_rgba(15,23,42,0.22)] backdrop-blur-md transition-transform hover:scale-[1.03] hover:bg-slate-950/34 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
        aria-label="Revenir à la vue initiale"
        title="Revenir à la vue initiale"
        disabled={!viewer || !initialCameraPose}
      >
        <House className="h-5 w-5" strokeWidth={2.2} />
      </button>

      {/* Bouton Zoom + */}
      <button
        type="button"
        onClick={() => zoomer("in")}
        className="flex h-12 w-12 cursor-pointer items-center justify-center rounded-2xl border border-white/25 bg-slate-950/28 text-white/92 shadow-[0_8px_18px_rgba(15,23,42,0.22)] backdrop-blur-md transition-transform hover:scale-[1.03] hover:bg-slate-950/34 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        disabled={!viewer}
        aria-label="Zoomer"
        title={viewer ? "Zoomer" : "Vue 3D indisponible"}
      >
        <Plus className="h-5 w-5" strokeWidth={2.4} />
      </button>

      {/* Bouton Zoom - */}
      <button
        type="button"
        onClick={() => zoomer("out")}
        className="flex h-12 w-12 cursor-pointer items-center justify-center rounded-2xl border border-white/25 bg-slate-950/28 text-white/92 shadow-[0_8px_18px_rgba(15,23,42,0.22)] backdrop-blur-md transition-transform hover:scale-[1.03] hover:bg-slate-950/34 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        disabled={!viewer}
        aria-label="Dézoomer"
        title={viewer ? "Dézoomer" : "Vue 3D indisponible"}
      >
        <Minus className="h-5 w-5" strokeWidth={2.4} />
      </button>
    </div>
  );
}
